import {
  IBenefitLineSelection,
  IBenefitLineSelector,
  IVocabularyRow,
} from "types/BenefitSelection/BenefitSelection";

import { serviceLineLabel } from "./constants";
import { fallbackOf } from "./mapper";

/**
 * CLE-2792 — a selector as a full sentence, with the LINE TYPE DEFINITIONS printed inline.
 *
 * 🚨 NOTHING HERE REQUIRES A CLICK-THROUGH TO UNDERSTAND. A row reading "lines of type Physician
 * line" tells a reader nothing they can check against a payer's response — the words are the
 * content, and the whole reason this feature exists is that the words used to be a constant in code
 * nobody could see. So every definition is printed beside the type, every time.
 *
 * Since review round 1 that includes the PAYER-SPECIFIC definitions: `GET /benefitLineVocabulary`
 * returns every row with its `payerId` and `payerName`, and there is nowhere else to send a reader
 * — the payer edit page no longer holds them.
 */

/** How one definition row reads: which payer it is for, and what it looks for. */
export const describeLineTypeDefinition = (row: IVocabularyRow): string => {
  const scope =
    row.payerId === null
      ? "Every payer"
      : row.payerName ?? `Payer #${row.payerId}`;
  const contains = row.matchKeywords.length
    ? `contains ${row.matchKeywords.join(" or ")}`
    : "contains nothing in particular";
  const notContains = row.excludeKeywords.length
    ? `does not contain ${row.excludeKeywords.join(" or ")}`
    : "no exclusions";
  const place = row.placeOfService
    ? `place of service ${row.placeOfService}`
    : "any place of service";
  return `${scope}: the line's message ${contains} · ${notContains} · ${place}`;
};

/** Every definition of one line type, in the order a reader should meet them: global first. */
export const definitionsOfLineType = (
  role: string,
  rows: IVocabularyRow[]
): IVocabularyRow[] =>
  rows
    .filter((row) => row.role === role)
    .sort((a, b) => {
      if (a.payerId === b.payerId) return 0;
      if (a.payerId === null) return -1;
      if (b.payerId === null) return 1;
      return (a.payerName ?? "").localeCompare(b.payerName ?? "");
    });

/** One selector, in words. Reads as a sentence whether or not it names a line type. */
export const describeSelector = (selector: IBenefitLineSelector): string => {
  if (selector.any === true) {
    return "Any in-network line on the service line above.";
  }

  const clauses: string[] = [];

  if (selector.source) {
    clauses.push(`on the ${serviceLineLabel(selector.source)} service line`);
  }
  if (selector.role) {
    clauses.push(`of type ${selector.role}`);
  }
  if (selector.placeOfService === "OWN") {
    clauses.push("whose place of service is this account's own service line");
  } else if (selector.placeOfService === null) {
    clauses.push("that name no place of service");
  } else if (Array.isArray(selector.placeOfService)) {
    clauses.push(
      `whose place of service is ${selector.placeOfService.join(" or ")}`
    );
  }
  if (selector.timePeriodQualifier?.length) {
    clauses.push(`reported per ${selector.timePeriodQualifier.join(" or ")}`);
  }
  if (selector.coverageLevel?.length) {
    clauses.push(`at coverage level ${selector.coverageLevel.join(" or ")}`);
  }
  if (selector.matchKeywords?.length) {
    clauses.push(`containing ${selector.matchKeywords.join(" or ")}`);
  }
  if (selector.excludeKeywords?.length) {
    clauses.push(`never containing ${selector.excludeKeywords.join(" or ")}`);
  }

  if (clauses.length === 0) {
    return "This selector states nothing, so it would match no line on any check.";
  }

  return `In-network lines ${clauses.join(", ")}.`;
};

/**
 * The last line of a benefit: what happens on a check where no selector matched.
 *
 * The two answers are a different quote to a patient, so the list says which one is in force rather
 * than leaving it to be read off the presence of a trailing entry.
 */
export const describeFallback = (selection: IBenefitLineSelection): string =>
  fallbackOf(selection) === "ANY_LINE"
    ? "If no selector matches: use any line on the service line."
    : "If no selector matches: no value.";

export const describeTieBreak = (
  selection: IBenefitLineSelection,
  benefit: string
): string => {
  const unit = benefit === "COINSURANCE_PERCENT" ? "percent" : "amount";
  if (selection.tieBreak === "FIRST") return "First in the response";
  if (selection.tieBreak === "LOWEST") return `Lowest ${unit}`;
  return `Highest ${unit}`;
};
